import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { talleresAPI, cursosAPI, paisesAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  BookOpen, Users, Shield, CheckCircle, 
  ArrowRight, FileText, Wrench, GraduationCap,
  ExternalLink, Scale, Plane
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../utils/translations';

// Brand assets - Nueva imagen C&M 2024
const LOGO_LIGHT = 'https://customer-assets.emergentagent.com/job_5612798b-7eab-4f57-bce1-b7ba348a317b/artifacts/qgvhhx6f_fav%202.jpeg';
const LOGO_DARK = 'https://customer-assets.emergentagent.com/job_5612798b-7eab-4f57-bce1-b7ba348a317b/artifacts/bga15mw2_logo_blanco_1.png';
const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_aerolib/artifacts/o53dabnr_PORTADA%20WEB%20C%26M.png';
const CAFE_IMAGE = 'https://customer-assets.emergentagent.com/job_aerolib/artifacts/dx36k6xw_me%20gusta%20esta%20foto%20si%20podes%20agregala%20en%20alguna%20parte.jpeg';
// URL del backend - usar URLs relativas en producción
const isLocalDev = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || 
   window.location.hostname.includes('preview.emergentagent.com'));
const BACKEND_URL = isLocalDev ? (process.env.REACT_APP_BACKEND_URL || '') : '';

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { language, t, selectedCountry, setSelectedCountry } = useLanguage();
  const [talleres, setTalleres] = useState({});
  const [cursos, setCursos] = useState([]);
  const [paises, setPaises] = useState([]);
  const [loading, setLoading] = useState(true);

  // Textos traducidos
  const txt = translations.home;

  // Función para navegar - si está logueado va directo, si no va a login
  const handleNavigate = (destination = '/panel') => {
    if (user) {
      navigate(destination);
    } else {
      navigate(`/login?redirect=${encodeURIComponent(destination)}`);
    }
  };

  // Click en un taller desde la home: SIEMPRE llevar al formulario de login
  // (no auto-loguear ni saltarse el formulario aunque haya sesión activa)
  const handleTallerClickHome = () => {
    navigate('/login');
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [talleresRes, cursosRes, paisesRes] = await Promise.all([
        talleresAPI.getPublicos(),
        cursosAPI.getAll({ estado: 'publicado' }),
        paisesAPI.getPublicos(),
      ]);
      setTalleres(talleresRes.data);
      setCursos(cursosRes.data.slice(0, 3));
      const items = (paisesRes.data.items || []).map(p => ({
        code: p.nombre,
        flagImg: `https://flagcdn.com/w80/${p.codigo.toLowerCase()}.png`,
        name: p.nombre,
        sigla: p.sigla_autoridad,
        comingSoon: !p.activo,
      }));
      setPaises(items);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const suscripciones = [
    {
      nivel: t(txt.statusReport),
      precio: t(txt.gratis),
      precioPromo: t(txt.promoTiempoLimitado),
      colorClass: 'bg-slate-50 border-slate-200',
      badgeClass: 'bg-slate-100 text-slate-700',
      buttonClass: 'bg-white text-slate-900 border border-slate-300 hover:bg-slate-50',
      features: [
        t(txt.usuarioPorTaller),
        t(txt.verEstadoDocs),
        t(txt.solicitarCompra),
        t(txt.notifActualizaciones)
      ],
      planKey: 'status_report'
    },
    {
      nivel: t(txt.estandar),
      precio: language === 'pt' ? 'Pagamento Mensal' : 'Pago Mensual',
      precioPromo: language === 'pt' ? 'Promo com débito automático' : 'Promo con débito automático',
      colorClass: 'bg-teal-50/50 border-teal-200',
      badgeClass: 'bg-teal-500 text-white',
      buttonClass: 'bg-teal-500 hover:bg-teal-600 text-white',
      features: [
        t(txt.dosUsuarios),
        t(txt.verImprimirDocs),
        t(txt.comprarManuales),
        t(txt.accesoBoletines)
      ],
      planKey: 'estandar'
    },
    {
      nivel: 'Standard Pro',
      precio: language === 'pt' ? 'Pagamento Mensal' : 'Pago Mensual',
      precioPromo: language === 'pt' ? 'Desconto por pagamento anual' : 'Descuento por pago anual',
      colorClass: 'bg-sky-50/50 border-sky-200',
      badgeClass: 'bg-sky-500 text-white',
      buttonClass: 'bg-sky-500 hover:bg-sky-600 text-white',
      popular: true,
      features: [
        t(txt.cincoUsuarios),
        t(txt.verImprimirDocs),
        t(txt.comprarManuales),
        t(txt.accesoBoletines)
      ],
      planKey: 'estandar_pro'
    },
    {
      nivel: t(txt.premium),
      precio: null,
      precioPromo: null,
      colorClass: 'bg-orange-50/50 border-orange-200',
      badgeClass: 'bg-orange-500 text-white',
      buttonClass: 'bg-orange-500 hover:bg-orange-600 text-white',
      features: [
        t(txt.diezUsuarios),
        t(txt.verImprimirDocs),
        t(txt.comprarManuales),
        t(txt.accesoBoletines),
        t(txt.soportePrioritario)
      ],
      planKey: 'premium'
    }
  ];

  // NOTA: lista de países ahora se carga desde la DB en loadData(), no más hardcoded

  return (
    <div className="min-h-screen">
      {/* Hero Section with Condor Background */}
      <section className="relative min-h-[650px] lg:min-h-[750px] flex items-end overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE} 
            alt="Cóndor sobre los Andes" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/80 to-slate-900/60" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-transparent to-slate-900/30" />
        </div>
        
        {/* Content - Todo junto: Isologo + Título */}
        <div className="relative max-w-7xl mx-auto px-4 pb-24 pt-20 w-full">
          <div className="max-w-2xl">
            {/* Isologo C&M - Alineado a la izquierda, tamaño limitado al ancho del título */}
            <div className="mb-4">
              <img 
                src="/images/logos/cm-isologo.png" 
                alt="C&M Servicios Aeronáuticos" 
                className="h-14 sm:h-16 lg:h-20 xl:h-24 w-auto transition-all duration-700 ease-out hover:scale-105 cursor-pointer"
                data-testid="hero-logo"
              />
            </div>
            
            {/* Título - "Servicios" (el C&M ya está en el isologo) */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl text-white mb-2 leading-tight" style={{ fontFamily: 'Michroma, sans-serif' }}>
              {language === 'pt' ? 'Serviços' : 'Servicios'}
            </h1>
            <p className="text-3xl sm:text-4xl lg:text-5xl text-teal-400 mb-4 leading-tight" style={{ fontFamily: 'Michroma, sans-serif' }}>
              {language === 'pt' ? 'Aeronáuticos' : 'Aeronáuticos'}
            </p>
            
            <p className="text-base sm:text-lg text-gray-300 mb-8 leading-relaxed">
              {t(txt.heroDescription)}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 relative z-10">
              <Button 
                size="lg" 
                className="bg-teal-500 hover:bg-teal-600 text-white shadow-lg shadow-teal-500/25" 
                asChild
              >
                <Link to="/suscripcion">
                  {t(txt.comenzarAhora)}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-white/30 text-white hover:bg-white/10 backdrop-blur-sm" 
                asChild
              >
                <Link to="/talleres">{t(txt.verTalleres)}</Link>
              </Button>
            </div>
          </div>
        </div>
        
        {/* Decorative gradient - transición suave al contenido blanco */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent pointer-events-none" />
      </section>

      {/* Tu oficina técnica en línea - Gestoría + SIGMA */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              Tu oficina técnica en línea
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto leading-relaxed">
              Tramitamos, asesoramos y gestionamos ante la Autoridad Aeronáutica para que tu operación no se
              detenga por un trámite.
              <span className="block mt-1 text-slate-500">Y esto es solo el comienzo — seguimos creciendo para ofrecerte soluciones integrales.</span>
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {/* Card 1 - Gestoría (Violeta premium) */}
            <div
              onClick={() => navigate('/gestoria')}
              className="group relative h-full cursor-pointer rounded-2xl overflow-hidden bg-gradient-to-br from-violet-700 via-purple-700 to-violet-900 text-white shadow-xl shadow-violet-900/30 transition-all duration-300 hover:shadow-2xl hover:shadow-violet-900/40 hover:-translate-y-1 flex flex-col"
              data-testid="home-card-gestoria"
            >
              {/* Decor blobs */}
              <div className="absolute -top-24 -right-24 w-72 h-72 bg-white/10 rounded-full blur-3xl group-hover:bg-white/15 transition-all" />
              <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-pink-500/20 rounded-full blur-3xl" />

              <div className="relative p-8 md:p-9 flex flex-col flex-1">
                {/* Badge "Disponible ahora" */}
                <div className="inline-flex items-center gap-1.5 self-start bg-white/15 backdrop-blur px-3 py-1 rounded-full border border-white/25 mb-6">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-xs font-medium tracking-wide">DISPONIBLE AHORA</span>
                </div>

                {/* Icon con glow */}
                <div className="relative w-fit mb-5">
                  <div className="absolute inset-0 bg-white/30 rounded-2xl blur-xl group-hover:bg-white/40 transition-all" />
                  <div className="relative bg-white/15 backdrop-blur border border-white/25 p-4 rounded-2xl group-hover:scale-105 transition-transform">
                    <Scale className="h-9 w-9 text-white" />
                  </div>
                </div>

                <h3 className="text-2xl md:text-[26px] font-bold mb-2 tracking-tight">Gestoría Aeronáutica</h3>
                <p className="text-violet-100/95 leading-relaxed mb-6 flex-1">
                  Trámites, habilitaciones y asesoramiento ante la ANAC. Tu oficina técnica, desde cualquier lugar del país.
                </p>

                {/* Chips de servicios */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {['Matriculación', 'Habilitación TAR', 'Licencia MMA', '+6 más'].map((s) => (
                    <span key={s} className="text-xs bg-white/10 backdrop-blur border border-white/20 px-2.5 py-1 rounded-full text-violet-100">{s}</span>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-white/15 mt-auto">
                  <span className="text-sm font-semibold">Conocer el servicio</span>
                  <span className="h-9 w-9 rounded-full bg-white text-violet-700 flex items-center justify-center group-hover:translate-x-1 transition-transform shadow-lg">
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </div>

            {/* Card 2 - SIGMA (Blanco y Negro, teaser monocromo — al clickear lleva a la sección a color) */}
            <Link
              to="/gestoria#sigma"
              className="group relative h-full rounded-2xl overflow-hidden bg-gradient-to-br from-neutral-900 via-neutral-800 to-black text-white shadow-xl shadow-black/30 transition-all duration-300 hover:shadow-2xl hover:shadow-black/40 hover:-translate-y-1 border border-neutral-700 hover:border-neutral-500 flex flex-col"
              data-testid="home-card-sigma"
            >
              {/* Decor sutil gris */}
              <div className="absolute -top-24 -right-16 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-neutral-700/0 via-neutral-400 to-neutral-700/0" />

              <div className="relative p-8 md:p-9 flex flex-col flex-1">
                {/* Badge Próximamente (grises) */}
                <div className="inline-flex items-center gap-1.5 self-start bg-white/5 border border-neutral-600 text-neutral-300 px-3 py-1 rounded-full mb-6">
                  <span className="h-1.5 w-1.5 rounded-full bg-neutral-400" />
                  <span className="text-xs font-semibold tracking-wider uppercase">Próximamente</span>
                </div>

                {/* Icon avioncito monocromo */}
                <div className="relative w-fit mb-5">
                  <div className="absolute inset-0 bg-white/10 rounded-2xl blur-xl" />
                  <div className="relative bg-black border-2 border-neutral-500/70 p-4 rounded-2xl shadow-lg">
                    <Plane className="h-9 w-9 text-neutral-200" strokeWidth={1.5} />
                  </div>
                </div>

                <p className="text-neutral-400 text-xs font-bold tracking-[0.2em] mb-1">SIGMA</p>
                <h3 className="text-2xl md:text-[26px] font-bold text-white mb-2 tracking-tight leading-tight">
                  Sistema de Gestión de<br className="hidden md:block" /> Mantenimiento Aeronáutico
                </h3>
                <p className="text-neutral-400 leading-relaxed mb-6 flex-1">
                  Una herramienta integral para el seguimiento operativo, documental y de mantenimiento de tu flota. En desarrollo.
                </p>

                {/* Chips features monocromo */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {['Vencimientos', 'Checklists', 'Mantenimientos', 'Gestión doc.'].map((s) => (
                    <span key={s} className="text-xs bg-white/5 border border-neutral-700 text-neutral-300 px-2.5 py-1 rounded-full">{s}</span>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-2 border-t border-neutral-700 mt-auto">
                  <span className="text-sm font-semibold text-neutral-300">Ver más sobre SIGMA</span>
                  <span className="h-9 w-9 rounded-full bg-white/10 border border-neutral-600 text-neutral-200 group-hover:bg-white/20 group-hover:translate-x-1 transition-all flex items-center justify-center">
                    <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              {t(txt.todoEnUnLugar)}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {t(txt.docTecnica)}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            <Card 
              className="text-center hover:shadow-xl transition-all duration-300 border-0 shadow-lg group cursor-pointer"
              onClick={() => handleNavigate('/panel/documentos?tab=manuales')}
            >
              <CardHeader>
                <div className="mx-auto bg-gradient-to-br from-teal-50 to-teal-100 p-4 rounded-2xl w-fit group-hover:scale-110 transition-transform duration-300">
                  <FileText className="h-8 w-8 text-teal-600" />
                </div>
                <CardTitle className="text-lg text-slate-800">{t(txt.manualesTecnicos)}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm">
                  {t(txt.manualesDesc)}
                </p>
              </CardContent>
            </Card>

            <Card 
              className="text-center hover:shadow-xl transition-all duration-300 border-0 shadow-lg group cursor-pointer"
              onClick={() => handleNavigate('/panel/documentos?tab=publicaciones_servicio')}
            >
              <CardHeader>
                <div className="mx-auto bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-2xl w-fit group-hover:scale-110 transition-transform duration-300">
                  <Shield className="h-8 w-8 text-orange-600" />
                </div>
                <CardTitle className="text-lg text-slate-800">{t(txt.directivasAD)}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm">
                  {t(txt.directivasDesc)}
                </p>
              </CardContent>
            </Card>

            <Card 
              className="text-center hover:shadow-xl transition-all duration-300 border-0 shadow-lg group cursor-pointer"
              onClick={() => handleNavigate('/panel/documentos?tab=publicaciones_servicio')}
            >
              <CardHeader>
                <div className="mx-auto bg-gradient-to-br from-teal-50 to-teal-100 p-4 rounded-2xl w-fit group-hover:scale-110 transition-transform duration-300">
                  <Wrench className="h-8 w-8 text-teal-600" />
                </div>
                <CardTitle className="text-lg text-slate-800">{t(txt.boletinesServicio)}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 text-sm">
                  {t(txt.boletinesDesc)}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Café Image Section - Inspirational */}
      <section className="py-16 lg:py-20 bg-gradient-to-r from-slate-900 via-teal-900 to-slate-900 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            {/* Text Content */}
            <div className="text-white order-2 lg:order-1">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4" style={{ fontFamily: 'Michroma, sans-serif' }}>
                {t(txt.tuDocumentacion)}
                <span className="block text-teal-400">{t(txt.siempreAMano)}</span>
              </h2>
              <p className="text-gray-300 text-base leading-relaxed mb-6">
                {t(txt.inspirationalText)}
              </p>
              <ul className="space-y-3">
                {[
                  t(txt.acceso247),
                  t(txt.docActualizada),
                  t(txt.busquedaRapida),
                  t(txt.notifDirectivas)
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="h-5 w-5 text-teal-400 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Image */}
            <div className="order-1 lg:order-2">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/30">
                <img 
                  src={CAFE_IMAGE} 
                  alt="Manual C&M con café" 
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/50 via-transparent to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              {t(txt.planesSuscripcion)}
            </h2>
            <p className="text-slate-600">{t(txt.eligePlan)}</p>
          </div>
          
          {/* Fila 1: 3 planes básicos */}
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mb-6">
            {suscripciones.slice(0, 3).map((plan, index) => (
              <Card 
                key={index} 
                className={`relative border-2 ${plan.colorClass} hover:shadow-xl transition-all duration-300`}
              >
                <CardHeader className="text-center pt-6">
                  <Badge className={`${plan.badgeClass} mx-auto mb-2`}>{plan.nivel}</Badge>
                  <div className="space-y-1">
                    {plan.precio && (
                      <p className="text-2xl font-bold text-slate-900">{plan.precio}</p>
                    )}
                    {plan.precioPromo && (
                      <p className="text-sm text-teal-600 font-medium">{plan.precioPromo}</p>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-6">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-teal-500 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`w-full ${plan.buttonClass || 'bg-slate-800 hover:bg-slate-900'}`}
                    asChild
                  >
                    <Link to={`/suscripcion?nivel=${plan.planKey}`}>
                      {t(txt.elegirPlan)}
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Fila 2: Premium destacado ancho completo */}
          {suscripciones[3] && (
            <Card className="relative border-2 border-orange-300 bg-gradient-to-r from-orange-50 to-amber-50 hover:shadow-xl transition-all duration-300 ring-2 ring-orange-400 ring-offset-2">
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-orange-500 hover:bg-orange-600 px-4 py-1">
                ⭐ {t(txt.masPopular)}
              </Badge>
              <CardContent className="pt-8 pb-6">
                <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                  <div className="text-center md:text-left flex items-center">
                    <Badge className="bg-orange-500 text-white text-lg px-4 py-1">{suscripciones[3].nivel}</Badge>
                  </div>
                  <ul className="flex flex-wrap justify-center md:justify-start gap-x-6 gap-y-2">
                    {suscripciones[3].features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                        <span className="text-sm text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className={`${suscripciones[3].buttonClass || 'bg-orange-500 hover:bg-orange-600'} px-8`}
                    asChild
                  >
                    <Link to={`/suscripcion?nivel=${suscripciones[3].planKey}`}>
                      {t(txt.elegirPlan)}
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Talleres by Country */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              {t(txt.talleresSuscriptos)}
            </h2>
            <p className="text-slate-600">{t(txt.confianEnNosotros)}</p>
          </div>
          
          <Tabs value={selectedCountry} onValueChange={setSelectedCountry} className="w-full">
            {/* Country flags selector - using images for better cross-platform support */}
            <TabsList className="flex flex-wrap justify-center gap-2 mb-8 bg-transparent h-auto p-2">
              {paises.map(country => (
                <TabsTrigger 
                  key={country.code}
                  value={country.code}
                  disabled={country.comingSoon}
                  className={`
                    flex flex-col items-center gap-1 px-4 py-3 rounded-xl transition-all duration-300
                    ${country.comingSoon 
                      ? 'opacity-40 cursor-not-allowed bg-slate-100' 
                      : 'bg-white shadow-md hover:shadow-lg data-[state=active]:bg-teal-500 data-[state=active]:text-white data-[state=active]:shadow-teal-500/25'
                    }
                  `}
                >
                  <img src={country.flagImg} alt={country.name} className="w-10 h-7 object-cover rounded shadow-sm" />
                  <span className="text-xs font-medium">{country.name}</span>
                  {country.comingSoon && <span className="text-[10px] text-slate-400">{t(txt.proximamente)}</span>}
                </TabsTrigger>
              ))}
            </TabsList>
            
            {paises.map(pais => (
              <TabsContent key={pais.code} value={pais.code}>
                {talleres[pais.code] ? (
                  <div className="space-y-8">
                    {Object.entries(talleres[pais.code])
                      .sort(([provA], [provB]) => provA.localeCompare(provB, language === 'pt' ? 'pt' : 'es'))
                      .map(([provincia, talleresProv]) => (
                      <div key={provincia}>
                        <h3 className="text-lg font-semibold mb-4 text-slate-700 flex items-center gap-2">
                          <div className="h-1 w-6 bg-teal-500 rounded-full" />
                          {provincia}
                        </h3>
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          {talleresProv.map((taller, idx) => (
                            <Card 
                              key={idx} 
                              className="hover:shadow-lg hover:border-teal-200 transition-all duration-300 border cursor-pointer"
                              onClick={handleTallerClickHome}
                              data-testid={`home-taller-card-${taller.id || idx}`}
                            >
                              <CardContent className="p-4">
                                <div className="flex items-center gap-3">
                                  {taller.logo_url ? (
                                    <img 
                                      src={taller.logo_url.startsWith('http') ? taller.logo_url : `${BACKEND_URL}${taller.logo_url}`} 
                                      alt="" 
                                      className="h-12 w-12 rounded-lg object-cover border"
                                      onError={(e) => { 
                                        e.target.onerror = null;
                                        e.target.src = '';
                                        e.target.style.display = 'none';
                                        e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                                      }}
                                    />
                                  ) : null}
                                  <div 
                                    className="h-12 w-12 bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg flex items-center justify-center"
                                    style={{ display: taller.logo_url ? 'none' : 'flex' }}
                                  >
                                    <Wrench className="h-6 w-6 text-teal-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm text-slate-800">{taller.razon_social}</p>
                                    <p className="text-xs text-teal-600 font-medium">ANAC: {taller.numero_anac}</p>
                                  </div>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 bg-slate-50 rounded-xl">
                    <Users className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">{t(txt.noHayTalleres)} {pais.name}</p>
                  </div>
                )}
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Cursos */}
      {cursos.length > 0 && (
        <section className="py-16 lg:py-20 bg-gradient-to-b from-slate-50 to-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900">{t(txt.formacionProfesional)}</h2>
                <p className="text-slate-600 mt-1">{t(txt.capacitacionAero)}</p>
              </div>
              <Button variant="outline" className="border-teal-200 text-teal-700 hover:bg-teal-50" asChild>
                <Link to="/capacitaciones">{t(txt.verTodos)}</Link>
              </Button>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {cursos.map(curso => (
                <Card key={curso.id} className="hover:shadow-xl transition-all duration-300 border-0 shadow-lg">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-3 rounded-xl">
                        <GraduationCap className="h-8 w-8 text-orange-500" />
                      </div>
                      <Badge className="bg-orange-100 text-orange-700 hover:bg-orange-200">
                        {curso.precio_usd ? `USD $${curso.precio_usd}` : t(txt.consultar)}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg text-slate-800 mt-3">{curso.nombre}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-4">{curso.descripcion}</p>
                    <Button className="w-full bg-slate-800 hover:bg-slate-900" asChild>
                      <Link to={`/capacitaciones?curso=${curso.id}`}>{t(txt.verDetalles)}</Link>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Institutional Links with Logos */}
      <section className="py-16 bg-gradient-to-r from-teal-900 via-teal-800 to-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-xl font-bold mb-8 text-center">{t(txt.instituciones)}</h2>
          
          <div className="flex flex-col items-center gap-4 max-w-4xl mx-auto">
            {/* Fila 1: ANAC Argentina y DINACIA (logos alargados/blancos) */}
            <div className="flex justify-center items-center gap-4 md:gap-8">
              {[
                { 
                  name: 'ANAC Argentina', 
                  url: 'https://www.anac.gob.ar/',
                  logo: 'https://customer-assets.emergentagent.com/job_aerolib/artifacts/kyv6rw0z_anac-SIN%20FONDO.png'
                },
                { 
                  name: 'DINACIA Uruguay', 
                  url: 'https://www.dinacia.gub.uy/',
                  logo: 'https://www.dinacia.gub.uy/sites/default/files/inline-images/pie-dinacia.svg',
                  noFilter: true
                }
              ].map((link, idx) => (
                <a 
                  key={idx}
                  href={link.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-300 group"
                >
                  <img 
                    src={link.logo} 
                    alt={link.name} 
                    className={`h-14 w-auto object-contain transition-all duration-300 ${
                      link.noFilter 
                        ? 'opacity-90 group-hover:opacity-100' 
                        : 'filter brightness-0 invert group-hover:brightness-100 group-hover:invert-0'
                    }`}
                  />
                  <span className="text-xs font-medium text-center group-hover:text-orange-400 transition-colors">
                    {link.name}
                  </span>
                </a>
              ))}
            </div>
            
            {/* Fila 2: ANAC Brasil, INMAE (centro), CEPIAyE */}
            <div className="flex justify-center items-center gap-4 md:gap-8">
              {[
                { 
                  name: 'ANAC Brasil', 
                  url: 'https://www.gov.br/anac/',
                  logo: '/images/logos/anac-brasil.png',
                  needsWhiteBg: true
                },
                { 
                  name: 'INMAE', 
                  url: 'https://inmae.faa.mil.ar/',
                  logo: '/images/logos/inmae.png',
                  needsWhiteBg: true
                },
                { 
                  name: 'CEPIAyE', 
                  url: 'https://cpiaye.org.ar/v2/inicio/',
                  logo: '/images/logos/cepiaye.png',
                  needsWhiteBg: true
                }
              ].map((link, idx) => (
                <a 
                  key={idx}
                  href={link.url} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all duration-300 group"
                >
                  <div className={`${link.needsWhiteBg ? 'bg-white rounded-lg p-2' : ''}`}>
                    <img 
                      src={link.logo} 
                      alt={link.name} 
                      className="h-12 w-auto object-contain transition-all duration-300 opacity-90 group-hover:opacity-100"
                    />
                  </div>
                  <span className="text-xs font-medium text-center group-hover:text-orange-400 transition-colors">
                    {link.name}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 text-gray-300">
        <div className="max-w-7xl mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Logo & About */}
            <div className="lg:col-span-1">
              <img 
                src="/images/logos/cm-footer-logo.png" 
                alt="C&M Servicios Aeronáuticos" 
                className="h-24 w-auto mb-4"
              />
              <p className="text-sm text-gray-400 leading-relaxed">
                {t(txt.footerDescription)}
              </p>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">{t(txt.contacto)}</h4>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2">
                  <span className="text-teal-400">📧</span>
                  <a href="mailto:biblioteca@cmpublicaciones.com" className="hover:text-teal-400 transition-colors">
                    biblioteca@cmpublicaciones.com
                  </a>
                </li>
                <li className="flex items-center gap-2 ml-6">
                  <span className="text-teal-400">📱</span>
                  <a href="tel:+543517327514" className="hover:text-teal-400 transition-colors">
                    +54 351 732-7514
                  </a>
                </li>
                <li className="flex items-center gap-2">
                  <span className="text-teal-400">🛠️</span>
                  <a href="mailto:soporte@cmpublicaciones.com" className="hover:text-teal-400 transition-colors">
                    soporte@cmpublicaciones.com
                  </a>
                </li>
                <li className="flex items-center gap-2 ml-6">
                  <span className="text-teal-400">📱</span>
                  <a href="tel:+543512500925" className="hover:text-teal-400 transition-colors">
                    +54 351 250-0925
                  </a>
                </li>
              </ul>
            </div>

            {/* Location */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">{t(txt.ubicacion)}</h4>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-teal-400 mt-0.5">📍</span>
                  <span>
                    Córdoba Capital<br />
                    Argentina<br />
                    CP: 5011
                  </span>
                </li>
              </ul>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">{t(txt.accesosRapidos)}</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/suscripcion" className="hover:text-teal-400 transition-colors">
                    {t(txt.solicitarSuscripcion)}
                  </Link>
                </li>
                <li>
                  <Link to="/login" className="hover:text-teal-400 transition-colors">
                    {t(translations.navbar.iniciarSesion)}
                  </Link>
                </li>
                <li>
                  <a href="https://www.anac.gob.ar/" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors">
                    ANAC Argentina
                  </a>
                </li>
                <li>
                  <a href="https://www.dinacia.gub.uy/" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors">
                    DINACIA Uruguay
                  </a>
                </li>
                <li>
                  <a href="https://www.gov.br/anac/pt-br" target="_blank" rel="noopener noreferrer" className="hover:text-teal-400 transition-colors">
                    ANAC Brasil
                  </a>
                </li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="border-t border-slate-800 pt-6">
            {/* Frase del Equipo Bibliotecario - Virtus et Scientia */}
            <div className="flex justify-center mb-4">
              <p 
                className="text-base md:text-lg text-teal-400/80 tracking-wider"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontStyle: 'italic' }}
              >
                "Virtus et Scientia"
              </p>
            </div>
            
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-xs text-gray-500">
                © {new Date().getFullYear()} C&M {language === 'pt' ? 'Serviços Aeronáuticos' : 'Servicios Aeronáuticos'}. {t(txt.derechosReservados)}
              </p>
              <p className="text-xs text-gray-600">
                {t(txt.desarrolladoPor)} <span className="text-teal-500">S.I.G. by Planet</span> - Sistema Integral de Gestión
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
